import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Leaf, Calendar, Sprout, Sparkles, MoonStar, Wheat } from "lucide-react";
import LocationDialog from "@/components/LocationDialog";
import EmailSubscribe from "@/components/EmailSubscribe";
import { useLocation as useUserLocation } from "@/lib/useLocation";

const nav = [
  { to: "/", label: "Sage", icon: Sparkles, end: true, testid: "nav-dashboard" },
  { to: "/planner", label: "Planner", icon: Calendar, testid: "nav-planner" },
  { to: "/garden", label: "My Garden", icon: Sprout, testid: "nav-garden" },
  { to: "/library", label: "Plant Library", icon: Leaf, testid: "nav-library" },
  { to: "/harvest", label: "Harvest", icon: Wheat, testid: "nav-harvest" },
];

export default function Layout({ children }) {
  const { pathname } = useLocation();
  const { location, setLocation } = useUserLocation();

  return (
    <div className="min-h-screen flex flex-col" data-testid="app-shell">
      <header className="sticky top-0 z-40 border-b border-border/70 bg-background/85 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-6">
          <NavLink to="/" className="flex items-center gap-2 group" data-testid="brand-logo">
            <span className="relative inline-flex items-center justify-center w-9 h-9 rounded-full bg-primary text-primary-foreground">
              <MoonStar className="w-4 h-4" strokeWidth={1.5} />
              <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-accent star-twinkle" />
            </span>
            <div className="leading-tight">
              <div className="font-serif text-lg tracking-tight">The Garden Sage</div>
              <div className="label-overline text-[10px]">a gardener's handbook</div>
            </div>
          </NavLink>

          <nav className="hidden md:flex items-center gap-1" data-testid="primary-nav">
            {nav.map((n) => {
              const Icon = n.icon;
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  data-testid={n.testid}
                  className={({ isActive }) =>
                    `inline-flex items-center gap-2 px-3.5 py-2 rounded-md text-sm transition-all duration-300 ${
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "text-foreground/70 hover:text-foreground hover:bg-muted/60"
                    }`
                  }
                >
                  <Icon className="w-4 h-4" strokeWidth={1.5} />
                  <span>{n.label}</span>
                </NavLink>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            <EmailSubscribe />
            <LocationDialog location={location} onSave={setLocation} />
          </div>
        </div>

        {/* Mobile nav */}
        <div className="md:hidden flex items-center justify-center gap-1 pb-2 px-3">
          {nav.map((n) => {
            const Icon = n.icon;
            const isActive = n.end ? pathname === n.to : pathname.startsWith(n.to);
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`m-${n.testid}`}
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs ${
                  isActive ? "bg-primary/10 text-primary" : "text-foreground/70"
                }`}
              >
                <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                {n.label}
              </NavLink>
            );
          })}
        </div>
      </header>

      <main className="flex-1 max-w-7xl mx-auto w-full px-6 lg:px-10 py-10">
        {children}
      </main>

      <footer className="border-t border-border/70 py-6 text-center">
        <span className="label-overline">Tend gently — the moon and rain are listening.</span>
      </footer>
    </div>
  );
}
